import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actions from '@/store/slices/quickPay.slice'; // Import your action creators
import * as api from '@/services/quickPay'; // Import your API functions
import { PayloadAction } from '@reduxjs/toolkit';
import { isBoolean } from 'lodash';
import { isSameRequestParams } from '@/utils/requestParamsChecker';

// Define action types
export interface GetQuickPayMasterProductAction extends PayloadAction<number> {
  type: 'quickPay/getQuickPayMasterProduct';
}

export interface GetQuickPayMasterProductsAction extends PayloadAction<number> {
  type: 'quickPay/getQuickPayMasterProducts';
}

export interface CreateQuickPayMasterProductAction extends PayloadAction<ICreateQuickPayMasterProduct> {
  type: 'quickPay/createQuickPayMasterProduct';
}

export interface UpdateQuickPayMasterProductAction extends PayloadAction<IQuickPayMasterProduct> {
  type: 'quickPay/updateQuickPayMasterProduct';
}

export interface DeleteQuickPayMasterProductAction extends PayloadAction<IDeleteQuickPayMasterProduct> {
  type: 'quickPay/deleteQuickPayMasterProduct';
}

// Define the shape of the API response for a single provider
export type IProductBarcode = {
  barcodeId: string,
  barcode: string,
  createdOn: string;
  updatedOn: string;
}
export interface IQuickPayMasterProduct {
  productId: string,
  pepsiProductId: string;
  barcodes: IProductBarcode[],
  createdOn: string;
  updatedOn: string;
}

export interface ICreateQuickPayMasterProduct {
  productId?: string;
  pepsiProductId?: string;
}

export interface ISelectedQuickPayMasterProduct {
  productId?: string;
  name?: string;
}

export interface IDeleteQuickPayMasterProduct {
  productId: string;
}

export interface IPagination {
  totalRecords: number;
  currentPage: number;
  totalPages: number;
  nextPage: number;
  previousPage: number;
}

export interface IApiResponse {
  success: boolean;
  message: string;
  data: IQuickPayMasterProduct[];
  pagination: IPagination;
}

function* getQuickPayMasterProducts(action: PayloadAction<api.IFetchQuickPayMasterProduct>): Generator {

  try {
    const response: any = yield call(api.fetchQuickPayMasterProducts, action?.payload);
    if (response?.data?.success || response?.data?.message === "Successfully got approved products.") {
      yield put(actions.getQuickPayMasterProductsSuccess(response?.data));
    } else {
      yield put(actions.getQuickPayMasterProductsFailure(response?.data));
    }
  } catch (error: any) {
    yield put(actions.getQuickPayMasterProductsFailure(error?.response?.data || { message: error?.message }));
  }
}

// Your saga function
function* getQuickPayMasterProduct(action: GetQuickPayMasterProductAction): Generator {
  try {
    const response: any = yield call(api.fetchQuickPayMasterProduct, action?.payload);
    const res = response?.data?.data

    if (isValidProviderResponse(res)) {
      yield put({
        type: 'quickPay/getQuickPayMasterProductSuccess',
        payload: res as IProductBarcode,
      });
    } else {
      yield put({ type: 'quickPay/getQuickPayMasterProductFailure', payload: response?.data });
    }
  } catch (error: any) {
    yield put({ type: 'quickPay/getQuickPayMasterProductFailure', payload: error?.response?.data || { message: error?.message } });
  }
}

function* createQuickPayMasterProduct(action: CreateQuickPayMasterProductAction) {
  try {
    const response: unknown = yield call(api.createQuickPayMasterProduct as any, action?.payload);
    if (isValidProviderResponse(response)) {
      yield put({
        type: 'quickPay/createQuickPayMasterProductSuccess',
        payload: response as IProductBarcode,
      });
    } else {
      yield put({ type: 'quickPay/createQuickPayMasterProductFailure', payload: (response as any)?.data });
    }
  } catch (error: any) {
    yield put({ type: 'quickPay/createQuickPayMasterProductFailure', payload: error?.response?.data || { message: error?.message } });
  }
}

function* updateQuickPayMasterProduct(action: UpdateQuickPayMasterProductAction) {
  try {
    const response: unknown = yield call(
      api.updateQuickPayMasterProduct as any,
      (action?.payload as any)?.providerId,
      action?.payload,
    );
    if (isValidProviderResponse(response)) {
      yield put({
        type: 'quickPay/updateQuickPayMasterProductSuccess',
        payload: response as IProductBarcode,
      });
    } else {
      yield put({ type: 'quickPay/updateQuickPayMasterProductFailure', payload: (response as any)?.data });
    }
  } catch (error: any) {
    yield put({ type: 'quickPay/updateQuickPayMasterProductFailure', payload: error?.response?.data || { message: error?.message } });
  }
}

function* deleteQuickPayMasterProduct(action: DeleteQuickPayMasterProductAction) {

  try {
    const response: unknown = yield call(api.deleteQuickPayMasterProduct as any, action?.payload);
    if (isValidProviderResponse(response)) {
      yield put({
        type: 'quickPay/deleteQuickPayMasterProductSuccess',
        payload: response as IProductBarcode,
      });
    } else {
      yield put({ type: 'quickPay/deleteQuickPayMasterProductFailure', payload: (response as any)?.data });
    }
  } catch (error: any) {
    yield put({ type: 'quickPay/deleteQuickPayMasterProductFailure', payload: error?.response?.data || { message: error?.message } });
  }
}

function isValidProviderResponse(
  response: unknown,
): response is IProductBarcode {
  return (
    typeof response === 'object' &&
    response !== null &&
    'barcodeId' in response &&
    'barcode' in response &&
    'createdOn' in response &&
    'updatedOn' in response
    // Add other properties if present in the API response
  );
}

function isValidProvidersResponse(response: IApiResponse): boolean {
  return (
    isBoolean(response.success) &&
    response.message === 'Successfully got list of providers.'
  );
}

function* quickPayMasterProductSaga(): Generator {
  // Watch for the specific action type
  yield takeLatest(actions.getQuickPayMasterProducts.type, getQuickPayMasterProducts);
  yield takeEvery<GetQuickPayMasterProductAction>('quickPay/getQuickPayMasterProduct', getQuickPayMasterProduct);
  yield takeEvery<CreateQuickPayMasterProductAction>(
    'quickPay/createQuickPayMasterProduct',
    createQuickPayMasterProduct,
  );
  yield takeEvery<UpdateQuickPayMasterProductAction>(
    'quickPay/updateQuickPayMasterProduct',
    updateQuickPayMasterProduct,
  );
  yield takeEvery<DeleteQuickPayMasterProductAction>(
    'quickPay/deleteQuickPayMasterProduct',
    deleteQuickPayMasterProduct,
  );
}

export default quickPayMasterProductSaga;
